import React, { Component } from 'react';
import {
  chain,
  filter,
  find,
  get,
  includes,
  isEmpty,
  map,
  omit,
  set,
  size,
  sortBy,
  startCase,
  sumBy,
  toLower,
  values,
  cloneDeep,
} from 'lodash';
import moment from 'moment';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { Button as NovaButton } from '@fashionnovateam/nova-ui-toolkit';

import Button from '../../../ReusableComponents/Button/Button';
import styles from '../../Deliveries.module.css';
import SelectablePurchaseOrder from './SelectablePurchaseOrder.tsx';
import SearchSelectablePurchaseOrders from './SearchSelectablePurchaseOrders';
import HeaderCell from '../../../ReusableComponents/Table/HeaderCell';
import HeaderRow from '../../../ReusableComponents/Table/HeaderRow';
import TableBody from '../../../ReusableComponents/Table/TableBody';
import Table from '../../../ReusableComponents/Table/Table';
import TableHead from '../../../ReusableComponents/Table/TableHead';
import makeTopStatuses from '../../../../Lib/Utils/Role/make-top-statuses';
import { FeatureFlagsContext } from '../../../../Lib/Contexts/FeatureFlagsContext.tsx';

const matchesSearch = (purchaseOrder, search) => {
  const poNumber = toLower(purchaseOrder.po_number);
  const status = toLower(purchaseOrder.status);

  return (
    includes(poNumber, toLower(search)) || includes(status, toLower(search))
  );
};

const getOrdersQuantity = (orders) =>
  chain(orders).flatMap('purchase_order_lines').sumBy('quantity').value();

const filterPurchaseOrders = (purchaseOrders, search, selectedIds, superuser) =>
  filter(values(purchaseOrders), (po) => matchesSearch(po, search));

const Locations = {
  SFS: 'santa fe springs',
  SEVILLE: 'seville',
  RIM: 'rim',
};

const allowZeroPurchaseOrders = (delivery) => {
  if (!delivery.id) return false;

  const differenceInDays = moment
    .utc(delivery.timeSlot)
    .startOf('day')
    .diff(moment.utc().startOf('day'), 'days');

  return differenceInDays <= 0;
};

const disableOrder = (order, totalUnits, location, delivery, selectedIds) => {
  const orderUnits = sumBy(order.purchase_order_lines, 'quantity');
  const maxUnits = location.max_units_per_vendor_per_day;
  const deliveryPastDue = moment
    .utc(delivery.timeSlot)
    .isBefore(moment.utc(moment().format('LLLL'), 'LLLL'));

  const topStatus = makeTopStatuses(order, false)[0];
  if (topStatus === 'Approved' && selectedIds.includes(order.id)) {
    return false;
  }
  if (
    totalUnits + orderUnits > maxUnits ||
    deliveryPastDue ||
    topStatus !== 'Approved'
  ) {
    return true;
  }

  return false;
};

const showQuickSubmit = (deliveryExists, vendorView, deliveryDate) => {
  if (!deliveryExists) return false;
  if (vendorView) return false;

  return moment
    .utc(deliveryDate)
    .isBefore(moment.utc(moment().format('LLLL'), 'LLLL'));
};

class PurchaseOrderSelector extends Component {
  constructor(props) {
    super(props);

    const { getPurchaseOrders, selected } = this.props;

    this.originallySelected = cloneDeep(selected);

    this.state = {
      selected,
      search: '',
      error: null,
      page: 0,
      pageSize: 100,
      location: null,
    };

    this.setLocation = this.setLocation.bind(this);
    this.setSearch = this.setSearch.bind(this);
    this.toggleSelect = this.toggleSelect.bind(this);
    this.next = this.next.bind(this);
    this.quickSubmit = this.quickSubmit.bind(this);

    getPurchaseOrders(undefined, {
      page: this.state.page,
      search: this.state.search,
    });
  }

  componentDidUpdate({ purchaseOrders: prevOrders }) {
    const { purchaseOrders, vendorView } = this.props;
    // When editing a delivery the location will update if the amount of POs changes
    // POs with items that had category shoes were delivered to a certain location
    // but this is not longer being used
    if (size(prevOrders) !== size(purchaseOrders) && vendorView) {
      this.setLocation();
    }
  }

  setLocation() {
    const { locations, setLocation, getPurchaseOrders, delivery } = this.props;
    const vendorDeliveryLocation = get(
      locations,
      get(delivery, 'vendor.default_delivery_location'),
    );

    if (vendorDeliveryLocation) {
      setLocation(vendorDeliveryLocation);
      getPurchaseOrders(get(vendorDeliveryLocation, 'id'), {
        page: this.state.page,
        search: this.state.search,
      });
      this.setState({ location: vendorDeliveryLocation });
    } else {
      const location = find(
        locations,
        ({ name }) => toLower(name) === Locations.RIM,
      );
      setLocation(location);
      getPurchaseOrders(get(location, 'id'), {
        page: this.state.page,
        search: this.state.search,
      });
      this.setState({ location });
    }
  }

  setSearch(search) {
    this.setState({ search });
  }

  toggleSelect(purchaseOrder, unSelected) {
    const { onSelect } = this.props;
    const { selected } = this.state;
    const { id } = purchaseOrder;

    if (unSelected) {
      const filtered = omit(selected, id);
      this.setState({ selected: filtered }, () => onSelect(filtered));
    } else {
      set(selected, id, purchaseOrder);
      this.setState({ selected }, () => onSelect(selected));
    }
  }

  quickSubmit(delivery) {
    const { saveDelivery } = this.props;
    const { selected } = this.state;
    const isValid = !isEmpty(selected);
    const allowZeroPO = allowZeroPurchaseOrders(delivery);

    if (!isValid && !allowZeroPO) {
      const error = `Please select at least 1 purchase order.`;

      this.setState({ error });
    } else {
      saveDelivery();
    }
  }

  next(delivery) {
    const { next } = this.props;
    const { selected } = this.state;
    const isValid = !isEmpty(selected);
    const allowZeroPO = allowZeroPurchaseOrders(delivery);

    if (!isValid && !allowZeroPO) {
      const error = `Please select at least 1 purchase order.`;

      this.setState({ error });
    } else {
      next();
    }
  }

  render() {
    const headers = [
      'PO Number',
      'Units',
      'Due Date',
      'TOP Status',
      'SIZE + WEIGHT',
      'Select',
    ];

    const {
      purchaseOrders,
      delivery,
      editing,
      onClose,
      locations,
      back,
      superuser,
      vendorUser,
      vendorView,
    } = this.props;
    const { selected, search, error } = this.state;
    const selectedIds = map(selected, 'id');
    const originallySelectedIds = map(this.originallySelected, 'id');
    const filteredPurchaseOrders = filterPurchaseOrders(
      purchaseOrders,
      search,
      originallySelectedIds,
      superuser,
    );
    const location = locations[delivery.location_id];
    const totalUnits = getOrdersQuantity(selected);
    const { flags } = this.context;

    return (
      <div className={styles.step}>
        <div className={styles.stepContainer}>
          <div className={cx(styles.stepHeader, styles.stepHeaderCentered)}>
            <h3>Select Purchase Orders For Delivery:</h3>
            <div className={cx(styles.stepDetails, styles.totalUnits)}>
              <div className={cx(styles.stepHeaderDetail)}>
                <span>Total Units on Selected Purchase Orders:</span>
                <span>{totalUnits}</span>
              </div>
              <div className={cx(styles.stepHeaderDetail)}>
                <span>Max Units Allowed in a Delivery:</span>
                <span>{location.max_units_per_vendor_per_day}</span>
              </div>
            </div>
          </div>

          <div className={styles.purchaseOrderFilters}>
            <SearchSelectablePurchaseOrders
              setSearch={(value) => {
                this.setSearch(value);
                if (flags.po_service && flags.po_service.enabled) {
                  this.setState({ page: 0 });
                  this.props.getPurchaseOrders(get(this.state.location, 'id'), {
                    page: 0,
                    search: value,
                  });
                }
              }}
            />
          </div>

          <div className={styles.stepBody}>
            <div className={styles.stepBodyColumn}>
              <Table fixed={false}>
                <TableHead>
                  <HeaderRow>
                    {headers.map((header) => (
                      <HeaderCell
                        key={header}
                        id={`header=${header}`}
                        className={styles.selectHeader}
                      >
                        {startCase(header)}
                      </HeaderCell>
                    ))}
                  </HeaderRow>
                </TableHead>

                <TableBody>
                  {map(
                    sortBy(filteredPurchaseOrders, ['due_date', 'po_number']),
                    (purchaseOrder, index) => (
                      <SelectablePurchaseOrder
                        key={`${index}${purchaseOrder.id}`}
                        index={index}
                        superuser={superuser}
                        toggleSelect={this.toggleSelect}
                        purchaseOrder={purchaseOrder}
                        vendorUser={vendorUser}
                        disabled={
                          !superuser &&
                          (originallySelectedIds.length > 0 ||
                            !includes(
                              originallySelectedIds,
                              purchaseOrder.id,
                            )) &&
                          disableOrder(
                            purchaseOrder,
                            totalUnits,
                            location,
                            delivery,
                            selectedIds,
                          )
                        }
                        selected={includes(selectedIds, purchaseOrder.id)}
                      />
                    ),
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {flags.po_service && flags.po_service.enabled ? (
            <div className={styles.paginationContainer}>
              <NovaButton
                color="secondary"
                size="sm"
                disabled={this.props.loading === true || this.state.page === 0}
                onClick={() => {
                  this.props.getPurchaseOrders(get(this.state.location, 'id'), {
                    page: this.state.page - 1,
                    search: this.state.search,
                  });
                  this.setState((prevState) => ({ page: prevState.page - 1 }));
                }}
              >
                Previous PO Page
              </NovaButton>
              <NovaButton
                color="secondary"
                size="sm"
                disabled={
                  this.props.loading === true ||
                  Object.keys(purchaseOrders).length < this.state.pageSize
                }
                onClick={() => {
                  this.props.getPurchaseOrders(get(this.state.location, 'id'), {
                    page: this.state.page + 1,
                    search: this.state.search,
                  });
                  this.setState((prevState) => ({ page: prevState.page + 1 }));
                }}
              >
                Next PO Page
              </NovaButton>
            </div>
          ) : null}

          <div className={styles.stepActions}>
            {!editing && !vendorView && (
              <Button
                onClick={back}
                color="primary"
                className={styles.backButton}
                variant="outlined"
              >
                Back
              </Button>
            )}

            <Button
              onClick={onClose}
              variant="text"
              className={styles.cancelButton}
              color="primary"
            >
              Cancel
            </Button>

            {showQuickSubmit(delivery.id, vendorView, delivery.timeSlot) && (
              <div>
                <Button onClick={() => this.quickSubmit(delivery)}>
                  Submit Changes
                </Button>
              </div>
            )}
            <Button
              onClick={() => this.next(delivery)}
              variant="contained"
              color="white"
              className={styles.nextButton}
            >
              Next
            </Button>
          </div>

          <div className={styles.error}>{error}</div>
        </div>
      </div>
    );
  }
}

PurchaseOrderSelector.propTypes = {
  back: PropTypes.func.isRequired,
  delivery: PropTypes.shape({
    location_id: PropTypes.string,
  }).isRequired,
  editing: PropTypes.bool.isRequired,
  getPurchaseOrders: PropTypes.func.isRequired,
  locations: PropTypes.shape({}).isRequired,
  loading: PropTypes.bool,
  next: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  setLocation: PropTypes.func,
  purchaseOrders: PropTypes.shape({}).isRequired,
  selected: PropTypes.shape({}),
  superuser: PropTypes.bool,
  vendorUser: PropTypes.shape({}),
  vendorView: PropTypes.bool,
  saveDelivery: PropTypes.func.isRequired,
};

PurchaseOrderSelector.contextType = FeatureFlagsContext;

PurchaseOrderSelector.defaultProps = {
  selected: {},
  setLocation: () => {},
  superuser: false,
  vendorUser: null,
  vendorView: false,
};

export default PurchaseOrderSelector;
